// @flow
import React, { useState, useEffect, useRef } from 'react';
import type { Element } from 'react';
import { withTranslation, useTranslation } from '@i18n';
import { connect } from 'react-redux';
import { compose } from 'redux';
import { FastField, Field } from 'formik';
import { withRouter } from 'next/router';
import InputPhone from '@components/InputPhone';
import Input from '@components/Input';
import ButtonRadio from '@components/ButtonRadio';
import Checkbox from '@components/Checkbox';
import { StyledInput } from '@styles/inputs';
import { isEmpty } from 'lodash';
import { withFormik } from '@lib';
import { FormFlowTimeline } from '@screens/ProfilePackageForm/index';
import { requestPackageDetails } from '@screens/Details/Package/redux/actions';
import { createCandidate, resetCandidateCreate } from '@redux/components/CandidateCreate/actions';
import { getInternationalPhone } from '@services/phone';
import type { GlobalState } from '@globalTypes';
import TagManager from 'react-gtm-module';
import { DATA_LAYER_NAME } from '@lib/constants';
import { PhoneCode } from '../ContactPageNew/countryCode';
import type { Props } from './types';

import {
  StyledCover,
  StyledFormDetialsWrapper,
  StyledHeadTitle,
  StyledHeadSubTitle,
  StyledItemGroup,
  StyledItem,
  StyledRadioGroup,
  StyledButtonSubmit,
  StyledButtonWrapper,
  StyledTermsAndConditionsWrapper,
  StyledLabelTextWrapper,
  StyledProfileLabel,
  InfoTimeLineWraper,
  StyledTimeLinePointer,
  StyledDashedLine,
  StyledTimeLineContent,
  StyledTImeLineText,
  StyledStepsTextWrapper
} from './styles';

const optionalFields = ['gender', 'country', 'notes'];
const mandatoryFields = ['first_name', 'last_name', 'email', 'phone_code', 'phone'];
const fieldNames = [...optionalFields, ...mandatoryFields];

const PersonalDetailsForm = ({
  t,
  amount,
  setFieldValue,
  packageData,
  handleSubmit,
  values,
  errors,
  created,
  handlePhoneChange,
  packageFormScreen,
  paymentScreen,
  isModal
}: Props): Element<any> => {
  const [checkboxChecked, setCheckboxChecked] = useState(true);
  const isAnyValueEmpty: boolean = mandatoryFields.some(fieldName => isEmpty(values[fieldName]));
  const withFormId = key => `package-${key}-modal`;
  useEffect(() => {
    fetch('https://ipapi.co/json')
      .then(res => res.json())
      .then((res) => {
        const internationalPhone: string = getInternationalPhone(PhoneCode[res.country_code], '');
        if (res.city) {
          setFieldValue('address_city', res.city || '');
        }
        if (res.country_code) {
          setFieldValue('phone', internationalPhone || '');
        }
        if (res.country_name) {
          setFieldValue('country', res.country_name || '');
        }
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    resetCandidateCreate(isModal);
  }, [isModal]);

  if (created) {
    trackCreated(packageData?.data?.attributes?.name);
  }

  const StepsData = t('StepsContent', { returnObjects: true }) || [];

  const InfoTimeLine = (
    <InfoTimeLineWraper>
      {StepsData.map((step, idx) => (
        <>
          <StyledTimeLineContent>
            <StyledTimeLinePointer first={idx === 0} />
          </StyledTimeLineContent>
          <StyledDashedLine />
        </>
      ))}
    </InfoTimeLineWraper>
  );

  const InfoTImeLineText = (
    <>
      <StyledStepsTextWrapper>
        {StepsData.map(step => (
          <StyledTImeLineText>{step}</StyledTImeLineText>
        ))}
      </StyledStepsTextWrapper>
    </>
  );

  return (
    <StyledCover packageFormScreen={packageFormScreen}>
      <StyledHeadTitle packageFormScreen={packageFormScreen}>
        {t('PatientMiniflow.Heading', { treatment: packageData?.data?.attributes?.name })}
      </StyledHeadTitle>
      {!packageFormScreen && (
        <>
          {InfoTImeLineText}
          {InfoTimeLine}
        </>
      )}

      <StyledFormDetialsWrapper packageFormScreen={packageFormScreen}>
        {packageFormScreen && (
          <>
            <FormFlowTimeline packageFormScreen={packageFormScreen} paymentScreen={paymentScreen} />
            <StyledHeadSubTitle>{t('PatientMiniflow.FillOutSubtitle')}</StyledHeadSubTitle>
          </>
        )}
        <StyledItemGroup>
          <StyledItem>
            <StyledRadioGroup packageFormScreen={packageFormScreen}>
              <Field
                name="gender"
                formValue="male"
                noBorder
                id={withFormId('male')}
                component={ButtonRadio}
                label={t('Mr')}
                light={packageFormScreen}
              />
              <Field
                name="gender"
                formValue="female"
                id={withFormId('female')}
                noBorder
                component={ButtonRadio}
                label={t('Ms')}
                light={packageFormScreen}
              />
            </StyledRadioGroup>
          </StyledItem>
        </StyledItemGroup>

        <StyledItemGroup>
          <StyledItem greyBlue={packageFormScreen}>
            <StyledProfileLabel packageFormScreen={packageFormScreen} htmlFor="first_name" required>
              {t('PatientMiniflow.FirstName')}
            </StyledProfileLabel>
            <FastField
              name="first_name"
              noborder
              component={Input}
              inputComponent={StyledInput}
              packageFormScreen={packageFormScreen}
              roundedMobile
              greyBlue={packageFormScreen}
            />
          </StyledItem>

          <StyledItem>
            <StyledProfileLabel packageFormScreen={packageFormScreen} htmlFor="last_name" required>
              {t('PatientMiniflow.LastName')}
            </StyledProfileLabel>
            <FastField
              name="last_name"
              component={Input}
              noborder
              inputComponent={StyledInput}
              roundedMobile
              packageFormScreen={packageFormScreen}
              greyBlue={packageFormScreen}
            />
          </StyledItem>
        </StyledItemGroup>

        <StyledItemGroup>
          <StyledItem>
            <StyledProfileLabel packageFormScreen={packageFormScreen} htmlFor="email" required>
              {t('PatientMiniflow.Email')}
            </StyledProfileLabel>
            <FastField
              name="email"
              component={Input}
              displayName={t('Email')}
              inputComponent={StyledInput}
              noborder
              packageFormScreen={packageFormScreen}
              roundedMobile
              greyBlue={packageFormScreen}
              listStyle
            />
          </StyledItem>
        </StyledItemGroup>
        <StyledItemGroup>
          <StyledItem>
            <StyledProfileLabel packageFormScreen={packageFormScreen} htmlFor="phone" required>
              {t('PatientMiniflow.PhoneNumber')}
            </StyledProfileLabel>
            <InputPhone
              id={withFormId('phone')}
              phoneCode={values.phone_code}
              field={{ value: values.phone }}
              noBorder
              placeholder=""
              error={errors?.phone}
              onChange={handlePhoneChange}
              greyBlue={packageFormScreen}
            />
          </StyledItem>
        </StyledItemGroup>
        <StyledItem>
          <StyledTermsAndConditionsWrapper>
            <Checkbox
              id={withFormId('checkboxTermsAndConditions')}
              checked={checkboxChecked}
              onChange={setCheckboxChecked}
              bgColor={!packageFormScreen}
              noBorder
            />
            <StyledLabelTextWrapper packageFormScreen={packageFormScreen}>
              {t('PatientMiniflow.TermsAndCondition')}
            </StyledLabelTextWrapper>
          </StyledTermsAndConditionsWrapper>
        </StyledItem>

        <StyledButtonWrapper>
          <StyledButtonSubmit
            full
            type="submit"
            id="ContactFormID"
            label={t('PatientMiniflow.Pay', { amount })}
            onClick={() => {
              handleSubmit();
            }}
            packageFormScreen={packageFormScreen}
            disabled={!checkboxChecked || isAnyValueEmpty || !(values.phone.length > 5)}
          />
        </StyledButtonWrapper>
      </StyledFormDetialsWrapper>
    </StyledCover>
  );
};

const PackageMiniflowComponent = ({
  amount,
  setFieldValue,
  packageData,
  handleSubmit,
  dirty,
  values,
  errors,
  packageFormScreen,
  isModal
}: Props) => {
  const { t } = useTranslation(['ProfilePackages']);
  const submitButtonRef = useRef();

  const handleSelect = (name, value) => {
    setFieldValue(name, value);
  };

  const handlePhoneChange = (value, data, event, formattedValue) => {
    setFieldValue('phone', formattedValue);
    if (data.countryCode) {
      setFieldValue('phone_code', data.countryCode.toUpperCase());
    }
    if (!values.country) {
      setFieldValue('country', data.name);
    }
  };

  const disabledSubmit: boolean = !dirty;

  return (
    <>
      <PersonalDetailsForm
        submitRef={submitButtonRef}
        t={t}
        isModal={isModal}
        amount={amount}
        disabledSubmit={disabledSubmit}
        packageData={packageData}
        setFieldValue={setFieldValue}
        handleSubmit={handleSubmit}
        values={values}
        errors={errors}
        handlePhoneChange={handlePhoneChange}
        onSelect={handleSelect}
        packageFormScreen={packageFormScreen}
      />
    </>
  );
};

const handleSubmit = (
  formValues,
  {
    props: {
      createCandidate,
      addressCity,
      country_name,
      hours,
      time,
      location,
      packageData,
      i18n: { language },
      router: { query }
    }
  }
): void => {
  createCandidate({
    ...formValues,
    preferred_city: addressCity || formValues.address_city,
    address_city: addressCity || formValues.address_city,
    slug: packageData?.data?.attributes?.slug,
    package_name: packageData?.data?.attributes?.name,
    password: formValues.password,
    hours,
    time,
    country_name,
    location,
    language,
    source: query.s
  });
};

const trackCreated = async (packageScreen) => {
  const { default: ReactPixel } = await import('react-facebook-pixel');
  ReactPixel.init('370952911555724');
  ReactPixel.trackCustom('LeadCreated');

  TagManager.dataLayer({
    dataLayerName: DATA_LAYER_NAME,
    dataLayer: {
      event: 'PatientCreated',
      packageScreen
    }
  });
};

const mapStateToProps = (
  {
    candidateCreate: {
      sending, created, error, created_patient_id
    }
  }: GlobalState,
  props: any
) => ({
  sending,
  created,
  error,
  created_patient_id,
  initialFormValues: {
    data: {
      attributes: {
        treatment: props.treatment || '',
        password: props.password,
        address_city: props.addressCity,
        address_country: props.country
      }
    }
  }
});

const mapDispatchToProps = {
  createCandidate,
  requestPackageDetails,
  resetCandidateCreate
};

const PackageMiniflow = compose(
  withRouter,
  connect(
    mapStateToProps,
    mapDispatchToProps
  ),
  withTranslation(['ProfilePackages']),
  withFormik({
    handleSubmit,
    fieldNames,
    reduxErrorKey: 'error',
    reduxInitialValuesKey: 'initialFormValues'
  })
)(PackageMiniflowComponent);

PackageMiniflow.getInitialProps = (): {} => ({
  namespacesRequired: ['ProfilePackages']
});

export default PackageMiniflow;
